import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions, Chip, TextField, Typography, Box, Button, Alert, LinearProgress
} from '@mui/material';
import DeleteConfirmation from '../delete-confirmation/DeleteConfirmation';
import emailRateLimitingService from '../../services/emailRateLimitingService';
import { getCurrentUserEmail } from '../../services/auth';
import './EmailPopup.css';

const isValidEmail = (email) => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email);

const EmailPopup = ({ open, onClose, toList = [] }) => {
  const [ccList, setCcList] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [ccError, setCcError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rateLimitStatus, setRateLimitStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [nextAllowedTime, setNextAllowedTime] = useState(null);

  // Check rate limits when component opens
  useEffect(() => {
    if (open) {
      checkRateLimits();
    }
  }, [open]);

  // Update countdown timer for next allowed time
  useEffect(() => {
    if (nextAllowedTime) {
      const interval = setInterval(() => {
        const now = Date.now();
        if (now >= nextAllowedTime) {
          setNextAllowedTime(null);
          checkRateLimits();
          clearInterval(interval);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [nextAllowedTime]);

  const checkRateLimits = async () => {
    try {
      const userEmail = getCurrentUserEmail();
      if (!userEmail) {
        setValidationErrors(['User not authenticated']);
        return;
      }

      const status = emailRateLimitingService.getStatus(userEmail);
      setRateLimitStatus(status);
      setQuotaInfo(status.remainingQuotas);

      if (status.isBlocked) {
        setValidationErrors(['Account temporarily blocked due to email abuse']);
      } else {
        setValidationErrors([]);
      }
    } catch (error) {
      console.error('Failed to check rate limits:', error);
      setValidationErrors(['Failed to verify email sending limits']);
    }
  };

  const validateEmailBeforeSend = async () => {
    setIsValidating(true);
    setValidationErrors([]);
    setNextAllowedTime(null);

    try {
      const userEmail = getCurrentUserEmail();
      if (!userEmail) {
        setValidationErrors(['User not authenticated']);
        setIsValidating(false);
        return false;
      }

      const allRecipients = [
        ...toList.map(item => item.EmailId || item.emailId),
        ...ccList
      ];

      const emailData = {
        recipients: allRecipients,
        subject: '8D Report Closure Notification',
        body: 'This email contains the 8D report closure notification.',
        attachments: []
      };

      const validation = await emailRateLimitingService.canSendEmail(userEmail, emailData);
      
      if (!validation.canSend) {
        setValidationErrors(validation.errors);
        if (validation.nextAllowedTime) {
          setNextAllowedTime(validation.nextAllowedTime);
        }
        setIsValidating(false);
        return false;
      }

      setQuotaInfo(validation.remainingQuota);
      setIsValidating(false);
      return true;

    } catch (error) {
      console.error('Email validation failed:', error);
      setValidationErrors(['Email validation failed. Please try again.']);
      setIsValidating(false);
      return false;
    }
  };

  const getTotalRecipientCount = () => {
    return toList.length + ccList.length;
  };

  const isMaxRecipientsReached = () => {
    return getTotalRecipientCount() >= 10;
  };

  const getRemainingTimeText = () => {
    if (!nextAllowedTime) return '';
    
    const remaining = Math.max(0, nextAllowedTime - Date.now());
    const seconds = Math.ceil(remaining / 1000);
    
    if (seconds > 60) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const handleAddCc = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = ccInput.trim().replace(/,$/, '');
      if (!val) return;
      if (!isValidEmail(val)) { setCcError('Please enter a valid email'); return; }
      if (ccList.includes(val)) { setCcInput(''); return; }
      setCcList(prev => [...prev, val]);
      setCcInput('');
      setCcError('');
    }
  };

  const handleRemoveCc = (email) => {
    setCcList(prev => prev.filter(e => e !== email));
  };

  const handleSend = async () => {
    const isValid = await validateEmailBeforeSend();
    if (isValid) {
      setConfirmOpen(true);
    }
  };

  const handleConfirmClose = async (result) => {
    setConfirmOpen(false);
    if (result === 'confirm') {
      try {
        const userEmail = getCurrentUserEmail();
        
        const allRecipients = [
          ...toList.map(item => item.EmailId || item.emailId),
          ...ccList
        ];

        const emailData = {
          recipients: allRecipients,
          subject: '8D Report Closure Notification',
          body: 'This email contains the 8D report closure notification.'
        };

        emailRateLimitingService.trackEmailSent(userEmail, emailData);
        onClose({ ccList, toList, isMailSent: true });
        
      } catch (error) {
        console.error('Failed to track email send:', error);
        onClose({ ccList, toList, isMailSent: true });
      }
    }
  };

  const handleCancel = () => {
    onClose({ ccList: [], toList: [], isMailSent: false });
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogContent>
          <div className="emailPop">
            {/* Rate Limiting Status */}
            {quotaInfo && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Email Limits Status</Typography>
                <Typography variant="body2">Remaining today: {quotaInfo.emailsPerDay} emails</Typography>
                <Typography variant="body2">Remaining this hour: {quotaInfo.emailsPerHour} emails</Typography>
                <Typography variant="body2">Recipients: {getTotalRecipientCount()}/10 per email</Typography>
                {quotaInfo.emailsPerDay <= 5 && (
                  <LinearProgress 
                    variant="determinate" 
                    value={(quotaInfo.emailsPerDay / 100) * 100} 
                    sx={{ mt: 1 }}
                    color={quotaInfo.emailsPerDay <= 2 ? 'error' : 'warning'}
                  />
                )}
              </Box>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Cannot Send Email:</Typography>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                </ul>
                {nextAllowedTime && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Next email allowed in: {getRemainingTimeText()}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Validation in progress */}
            {isValidating && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Validating email limits...
              </Alert>
            )}

            {/* Max recipients warning */}
            {isMaxRecipientsReached() && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Maximum recipients limit reached (10). Remove some recipients to add more.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>To</Typography>
            <Box className="chipList">
              {toList.map((item, i) => (
                <Chip key={i} label={item.EmailId || item.emailId} size="small" />
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>CC</Typography>
            <Box className="chipList">
              {ccList.map((email, i) => (
                <Chip key={i} label={email} size="small" onDelete={() => handleRemoveCc(email)} />
              ))}
            </Box>
            <TextField
              className="ccInput"
              placeholder="Type CC email and press Enter"
              value={ccInput}
              onChange={(e) => { setCcInput(e.target.value); setCcError(''); }}
              onKeyDown={handleAddCc}
              error={!!ccError}
              helperText={ccError || (isMaxRecipientsReached() ? 'Maximum recipients reached' : '')}
              disabled={isMaxRecipientsReached()}
              fullWidth
              size="small"
              variant="standard"
            />

            <div className="sendEmail">
              <button 
                onClick={handleSend} 
                disabled={isValidating || validationErrors.length > 0 || nextAllowedTime !== null || getTotalRecipientCount() === 0}
              >
                {isValidating ? 'Validating...' : 'Send'}
              </button>
              <button className="cancelBtn" onClick={handleCancel}>Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmation
        open={confirmOpen}
        onClose={handleConfirmClose}
        msg="Are you sure you want to close 8D and send mail?"
      />
    </>
  );
};

export default EmailPopup;
